import { roundHalfUp } from './math.js';
/** Weighed bowl values stay in the original; every display uses the same net grams. */
export function mealWeights(meal) {
    const bowl = meal.bowlG ?? 0;
    return { servedG: roundHalfUp(meal.servedG - bowl, 2), leftoverG: meal.leftoverG == null ? null : roundHalfUp(meal.leftoverG - bowl, 2) };
}
export function wetFoodWaterMl(eatenG) { return roundHalfUp(eatenG * 0.75); }
export function computeIntake(meal) {
    if (meal.leftoverG == null)
        return { ok: true, eatenG: null, derivedWaterMl: null, totalWaterMl: meal.waterMl ?? null };
    const eatenG = roundHalfUp(meal.servedG - meal.leftoverG, 2);
    if (eatenG < 0)
        return { ok: false, reason: 'leftoverExceedsServed' };
    const derivedWaterMl = meal.foodType === 'wet' || meal.foodType === 'prescriptionWet' ? wetFoodWaterMl(eatenG) : 0;
    return { ok: true, eatenG, derivedWaterMl, totalWaterMl: meal.waterMl == null ? null : meal.waterMl + derivedWaterMl };
}
/** 그릇에 채운 양 − 남은 양. 남은 양을 아직 안 적었으면 null(0으로 보지 않음). 증발은 보정하지 않는다. */
export function bowlDecreaseMl(log) {
    if (log.leftMl == null)
        return null;
    return Math.max(0, roundHalfUp(log.filledMl - log.leftMl));
}
/**
 * 그날 그릇에서 줄어든 물 + 식사에 적은 물 + 습식 사료 수분을 출처와 함께 합친다.
 * 식사 1건 단위 수분은 computeIntake와 같은 계산(습식 75%)을 쓴다. 아무 출처도 없으면 totalMl은 null.
 */
export function dailyWaterTotal(date, meals, waterLogs, dateOf) {
    const bowl = waterLogs.filter((log) => !log.deletedAt && log.date === date).map(bowlDecreaseMl).filter((value) => value != null);
    const bowlMl = bowl.length ? bowl.reduce((sum, value) => sum + value, 0) : null;
    let mealWaterMl = 0;
    let wetFoodMl = 0;
    let mealSources = 0;
    let mealWaterSources = 0;
    let wetSources = 0;
    for (const log of waterLogs)
        if (!log.deletedAt && log.date === date && log.wetFoodG != null) {
            wetFoodMl += wetFoodWaterMl(log.wetFoodG);
            mealSources += 1;
            wetSources += 1;
        }
    for (const meal of meals) {
        if (meal.deletedAt || dateOf(meal.servedAt) !== date)
            continue;
        const intake = computeIntake(meal);
        if (!intake.ok)
            continue;
        if (meal.waterMl != null) {
            mealWaterMl += meal.waterMl;
            mealSources += 1;
            mealWaterSources += 1;
        }
        if ((meal.foodType === 'wet' || meal.foodType === 'prescriptionWet') && intake.derivedWaterMl != null) {
            wetFoodMl += intake.derivedWaterMl;
            mealSources += 1;
            wetSources += 1;
        }
    }
    const sources = [
        ...(bowlMl != null ? [{ label: '그릇', ml: bowlMl }] : []),
        ...(mealWaterSources ? [{ label: '식사에 적은 물', ml: roundHalfUp(mealWaterMl) }] : []),
        ...(wetSources ? [{ label: '습식', ml: roundHalfUp(wetFoodMl) }] : []),
    ];
    const totalMl = bowlMl == null && !mealSources ? null : roundHalfUp((bowlMl ?? 0) + mealWaterMl + wetFoodMl);
    return { date, bowlMl, mealWaterMl: roundHalfUp(mealWaterMl), wetFoodMl: roundHalfUp(wetFoodMl), totalMl, sources };
}
/**
 * R22 — 사료별로 최근 기록 수, 준 양 합, 먹은 양 합, 남긴 비율. 남긴 양을 안 적은 식사는 비율에서 빼고
 * "남긴 양 미확인 n회"로 따로 센다. 평가어 없이 숫자만.
 */
export function summarizeByFood(meals, from, to = Number.POSITIVE_INFINITY) {
    const map = new Map();
    for (const meal of meals) {
        if (meal.deletedAt || meal.servedAt < from || meal.servedAt > to)
            continue;
        const name = meal.foodName.trim() || '사료';
        const row = map.get(name) ?? { foodName: name, count: 0, servedG: 0, eatenG: 0, leftoverRatio: null, leftoverUnknown: 0, knownServed: 0 };
        const weights = mealWeights(meal);
        const intake = computeIntake(meal);
        row.count += 1;
        row.servedG += weights.servedG;
        if (weights.leftoverG == null || !intake.ok || intake.eatenG == null)
            row.leftoverUnknown += 1;
        else {
            row.eatenG += intake.eatenG;
            row.knownServed += weights.servedG;
        }
        map.set(name, row);
    }
    return [...map.values()].map(({ knownServed, ...row }) => ({ ...row, servedG: roundHalfUp(row.servedG, 1), eatenG: row.count === row.leftoverUnknown ? null : roundHalfUp(row.eatenG, 1), leftoverRatio: knownServed > 0 ? roundHalfUp(((knownServed - row.eatenG) / knownServed) * 100, 0) : null })).sort((a, b) => b.count - a.count || a.foodName.localeCompare(b.foodName, 'ko'));
}
